import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  MetaAccountPicker,
  MetaLinkError,
  MetaLinkSuccess,
  type MetaLinkErrorKind,
} from '@/components/features/MetaAdsLinker';
import { LoadingSpinner } from '@/components/ui';
import { SESSION_KEYS } from '@/config/constants';
import { useAuth } from '@/hooks/useAuth';
import { metaAdsApi, type MetaAdAccountSummary } from '@/services/api/metaAds';
import { useAuthStore } from '@/store/authStore';
import { debug } from '@/utils/debug';
import emailStyles from '@/components/features/GoogleAdsLinker/EmailConfirmation.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

// Buckets where re-running the same query has any chance of working.
// State-related buckets need the user to start over from /account; retry
// from the callback URL is pointless because the state token is dead.
// `setup_failed` is retryable — we re-trigger completeSetup in place
// (the link itself has already succeeded, so retry stays on this page
// without going back to the account picker).
// `unknown` is NOT retryable: it fires when the backend returned
// success=false with an unrecognized reason_kind, and every current
// success=false path hard-deletes the credential — retry would 404
// (Copilot round-20 #VVV).
const RETRYABLE_BUCKETS: ReadonlySet<MetaLinkErrorKind> = new Set([
  'meta_api_error',
  'setup_failed',
]);

type Phase =
  | { kind: 'restoring_auth' }
  | { kind: 'fetching_accounts' }
  | { kind: 'picking_account'; accounts: MetaAdAccountSummary[] }
  | { kind: 'syncing'; account: MetaAdAccountSummary }
  | { kind: 'success'; account: MetaAdAccountSummary }
  | { kind: 'error'; error: MetaLinkErrorKind; serverMessage?: string };

interface PreludeOk {
  ok: true;
  state: string;
}
interface PreludeErr {
  ok: false;
  error: MetaLinkErrorKind;
}
type PreludeResult = PreludeOk | PreludeErr;

/**
 * Parse `#state=<token>` off the location hash and validate against
 * sessionStorage. Returns the state string or an error bucket.
 *
 * Why sessionStorage in addition to backend state validation: defense in
 * depth. The backend also validates state on every call, so missing
 * sessionStorage (private mode, cleared by user) is not a hard block — we
 * skip the local check silently in that case. A mismatch, however, is a
 * clear signal that the URL was not the one we minted.
 */
function runValidationPrelude(memberUuid: string | null): PreludeResult {
  // Authentication check only. Meta feature-gating is enforced at
  // `/auth/meta/authorize` server-side (via `require_new_meta_linkage_allowed`)
  // — a user who couldn't mint a state token also can't have arrived
  // here with a valid one. The old per-UUID beta gate was removed with
  // the free-tier flip; if the user is authenticated at all, they're
  // eligible to complete a callback they legitimately started. The
  // `'gate'` error kind still exists in the type union because
  // `bucketFromAxios` maps a backend 403 to it, but the prelude no
  // longer produces it.
  if (!memberUuid) {
    return { ok: false, error: 'gate' };
  }
  const hash = window.location.hash || '';
  const params = new URLSearchParams(
    hash.startsWith('#') ? hash.slice(1) : hash,
  );
  const state = params.get('state');
  if (!state) {
    return { ok: false, error: 'invalid_state' };
  }
  const stored = window.sessionStorage.getItem(SESSION_KEYS.META_OAUTH_STATE);
  // Per the Storage spec getItem returns string | null, but be tolerant of
  // undefined too (test-environment storage mocks sometimes return it).
  if (stored != null && stored !== state) {
    return { ok: false, error: 'invalid_state' };
  }
  return { ok: true, state };
}

function bucketFromAxios(err: unknown): {
  kind: MetaLinkErrorKind;
  serverMessage?: string;
} {
  if (err instanceof AxiosError) {
    const status = err.response?.status;
    const detailRaw = err.response?.data?.detail;
    const detail = typeof detailRaw === 'string' ? detailRaw : undefined;
    if (status === 400) {
      return { kind: 'state_expired_or_consumed', serverMessage: detail };
    }
    if (status === 403) {
      return { kind: 'gate', serverMessage: detail };
    }
    if (status && status >= 500) {
      return { kind: 'meta_api_error', serverMessage: detail };
    }
  }
  return { kind: 'unknown' };
}

/**
 * Meta OAuth round-trip target (mounted at /oauth/callback/meta).
 *
 * Backend redirects here with `#state=<token>` after exchanging the OAuth
 * code for a long-lived token (which is stashed on `oauth_state` rather
 * than persisted to `meta_ads_credentials` until the user picks an
 * account). This page owns the phase machine that drives the
 * MetaAdsLinker screens — see plan §4.2 / §16.0.
 *
 * Parallel of `GoogleAdsLinker/PollingManager.tsx`. The Meta flow has no
 * polling (callback is synchronous), so this is more linear: restore auth
 * → validate → fetch accounts → pick → success.
 *
 * Auth restore note: this route is mounted at the top level (not under
 * `ProtectedRoute`), so the usual ProtectedRoute-driven `checkAuth()` does
 * not run before the page paints. The full-page OAuth redirect can also
 * cold-load the SPA with an empty zustand store even though tokens are
 * still in localStorage. We kick `checkAuth()` ourselves and wait for the
 * store to repopulate before running the gate prelude — otherwise the
 * prelude reads `memberUuid=null` on first paint and incorrectly renders
 * the gate-blocked error.
 */
export function MetaOAuthCallback() {
  const navigate = useNavigate();
  const { memberUuid, checkAuth } = useAuth();
  const [authRestoreAttempted, setAuthRestoreAttempted] = useState(false);

  useEffect(() => {
    // Run once on mount. If there's a token sitting in localStorage but the
    // zustand store hasn't rehydrated yet, fire checkAuth and let the store
    // populate memberUuid; if there's no token, mark restore attempted so
    // the prelude can run and render the gate-blocked error appropriately.
    let cancelled = false;
    const token = window.localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
    if (memberUuid || !token) {
      setAuthRestoreAttempted(true);
      return;
    }
    void checkAuth().finally(() => {
      if (!cancelled) setAuthRestoreAttempted(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prelude = useMemo(
    () =>
      authRestoreAttempted
        ? runValidationPrelude(memberUuid)
        : null,
    [authRestoreAttempted, memberUuid],
  );

  const stateToken = prelude?.ok ? prelude.state : null;
  const accountsQuery = useQuery({
    queryKey: ['meta-oauth-accounts', stateToken],
    queryFn: () => metaAdsApi.getAccountsForState(stateToken!),
    enabled: prelude?.ok === true,
    retry: false,
    staleTime: Infinity,
  });

  const queryClient = useQueryClient();

  const [linkedAccount, setLinkedAccount] =
    useState<MetaAdAccountSummary | null>(null);
  // Account whose link succeeded; we're now running the backfill trigger
  // (POST /meta/complete-setup) before showing the final success modal.
  // Separate from linkedAccount so the success modal only renders once
  // completeSetup resolves (or times out, which we treat as success).
  const [syncingAccount, setSyncingAccount] =
    useState<MetaAdAccountSummary | null>(null);
  // Tick to re-trigger the completeSetup effect when the user clicks Retry
  // on a setup_failed error. useEffect doesn't re-run on the same
  // syncingAccount value, so we bump this counter to force it.
  const [setupAttempt, setSetupAttempt] = useState(0);
  const [linkError, setLinkError] = useState<{
    kind: MetaLinkErrorKind;
    serverMessage?: string;
  } | null>(null);

  useEffect(() => {
    if (linkedAccount && memberUuid) {
      debug.log('MetaOAuthCallback: Link succeeded, refreshing auth store');
      window.sessionStorage.removeItem(SESSION_KEYS.META_OAUTH_STATE);
      void useAuthStore.getState().preloadLinkedAccounts(memberUuid);
      // POST /meta/complete-setup regenerates app_configuration inline so the
      // saved campaign_list now carries Meta entries. Drop the cached copies
      // of every surface that reads the unified list so the next read fetches
      // fresh — `removeQueries` (not `invalidateQueries`) because:
      // - useCampaignFromCache reads via queryClient.getQueryData() which
      //   returns the cached value synchronously; invalidate would only
      //   mark stale and (by default) refetch active observers, leaving the
      //   synchronous reader on a stale snapshot.
      // - The next mount of any of these query keys (Account page, the
      //   /meta-campaign/{id} detail page, the Home dashboard) finds no
      //   cache entry and fetches fresh via fetchQuery's deduping path.
      // Four keys cover the unified list + per-campaign details.
      queryClient.removeQueries({ queryKey: ['app-config', memberUuid] });
      queryClient.removeQueries({ queryKey: ['campaigns'] });
      queryClient.removeQueries({ queryKey: ['meta-campaigns'] });
      // Singular per-campaign detail cache too — if the user navigates
      // directly to /meta-campaign/{id} for a campaign that existed pre-link,
      // useMetaCampaignQuery would otherwise serve a stale detail.
      queryClient.removeQueries({ queryKey: ['meta-campaign'] });
      // useCampaignFromCache materializes DERIVED Campaign objects under
      // ['campaign-from-cache', campaignId]. These are downstream of the
      // raw caches above — clearing those won't transitively invalidate
      // the derived ones since useCampaignFromCache reads via getQueryData
      // (synchronous, no observer wiring). Drop explicitly.
      queryClient.removeQueries({ queryKey: ['campaign-from-cache'] });
    }
  }, [linkedAccount, memberUuid, queryClient]);

  // Mirrors Google's PollingManager.onSuccess pattern
  // (GoogleAdsLinker/PollingManager.tsx:55-83): after linkAccount returns
  // we trigger completeSetup to kick off the 90-day backfill, then advance
  // to the success modal.
  //
  // Outcomes:
  //   - 2xx: setLinkedAccount → 'success' phase (final success modal).
  //   - Client-side timeout (axios ECONNABORTED): the link IS real, the
  //     Cloud Run job is likely still running. Treat as success — the
  //     backfill typically completes shortly after; the daily ingest
  //     backstops any account that didn't finish in the wait window.
  //   - 4xx/5xx: setLinkError({ kind: 'setup_failed' }) → 'error' phase
  //     with Retry CTA that bumps setupAttempt to re-run this effect.
  useEffect(() => {
    if (!syncingAccount || linkedAccount || linkError) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await metaAdsApi.completeSetup(
          syncingAccount.meta_ad_account_id,
        );
        if (cancelled) return;
        // Backend returns 200+success=false with a reason_kind discriminator.
        // Every success=false path hard-deletes the credential server-side
        // — retry-in-place would 404. All known kinds are terminal on this
        // URL; the user restarts from /account. Absent reason_kind →
        // back-compat fallback to threshold_rejected. Unrecognized value
        // → 'unknown' bucket, which is also terminal (see the terminal-
        // bucket set below) so a future backend reason_kind doesn't
        // silently become retryable. Copilot round-20 #VVV.
        if (response.success === false) {
          let kind: MetaLinkErrorKind;
          if (response.reason_kind === 'sync_failed') {
            kind = 'sync_failed';
          } else if (response.reason_kind === 'verify_failed') {
            kind = 'verify_failed';
          } else if (response.reason_kind === 'currency_rejected') {
            kind = 'currency_rejected';
          } else if (response.reason_kind === 'already_connected') {
            kind = 'already_connected';
          } else if (
            !response.reason_kind ||
            response.reason_kind === 'threshold_rejected'
          ) {
            kind = 'threshold_rejected';
          } else {
            kind = 'unknown';
          }
          setLinkError({ kind, serverMessage: response.message });
          return;
        }
        setLinkedAccount(syncingAccount);
      } catch (err) {
        if (cancelled) return;
        const isClientTimeout =
          err instanceof AxiosError && err.code === 'ECONNABORTED';
        if (isClientTimeout) {
          // Previously this branch treated timeout as success. That is
          // no longer safe: the backend can still run the aggregate-
          // spend gate after we give up, and a gate rejection hard-
          // deletes the credential. Silent-success would flash "Meta
          // linked!" while the credential was actually gone. Route to
          // an indeterminate error kind that sends the user to /account
          // to check the live state instead of guessing.
          debug.log(
            'MetaOAuthCallback: completeSetup timed out client-side; ' +
              'backend may still complete + potentially reject/delete — ' +
              'treating as indeterminate',
          );
          setLinkError({ kind: 'setup_indeterminate' });
          return;
        }
        debug.error('MetaOAuthCallback: completeSetup failed', err);
        const serverMessage =
          err instanceof AxiosError && typeof err.response?.data?.detail === 'string'
            ? err.response.data.detail
            : undefined;
        setLinkError({ kind: 'setup_failed', serverMessage });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [syncingAccount, setupAttempt, linkedAccount, linkError]);

  const phase: Phase = (() => {
    if (prelude === null) {
      return { kind: 'restoring_auth' };
    }
    if (!prelude.ok) {
      return { kind: 'error', error: prelude.error };
    }
    if (linkError) {
      return {
        kind: 'error',
        error: linkError.kind,
        serverMessage: linkError.serverMessage,
      };
    }
    if (linkedAccount) {
      return { kind: 'success', account: linkedAccount };
    }
    if (syncingAccount) {
      return { kind: 'syncing', account: syncingAccount };
    }
    if (accountsQuery.isLoading || accountsQuery.isFetching) {
      return { kind: 'fetching_accounts' };
    }
    if (accountsQuery.isError) {
      const bucket = bucketFromAxios(accountsQuery.error);
      return {
        kind: 'error',
        error: bucket.kind,
        serverMessage: bucket.serverMessage,
      };
    }
    if (accountsQuery.data) {
      return { kind: 'picking_account', accounts: accountsQuery.data };
    }
    return { kind: 'fetching_accounts' };
  })();

  // Match Google's PollingManager: success / non-gate-error CTAs ("Continue
  // to Dashboard" / "Back to Dashboard") send the user home; only the
  // gate-blocked screen ("Return to Account") routes to /account.
  const goToDashboard = () => navigate('/dashboard');
  const goToAccount = () => navigate('/account');

  if (
    phase.kind === 'restoring_auth' ||
    phase.kind === 'fetching_accounts' ||
    phase.kind === 'syncing'
  ) {
    const title =
      phase.kind === 'restoring_auth'
        ? 'Restoring your session…'
        : phase.kind === 'fetching_accounts'
          ? 'Fetching Your Meta Ad Accounts'
          : 'Setting Up Your Account';
    const subtitle =
      phase.kind === 'restoring_auth'
        ? 'One moment.'
        : phase.kind === 'fetching_accounts'
          ? 'One moment while we talk to Meta.'
          : 'Account linked! Please hold tight for a few minutes as we set up your account…';
    return (
      <div
        className={modalStyles.modalOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="meta-oauth-loading-title"
      >
        <div className={modalStyles.modal}>
          <div className={modalStyles.modalHeader}>
            <h3
              id="meta-oauth-loading-title"
              className={modalStyles.modalTitle}
            >
              {title}
            </h3>
          </div>
          <div className={modalStyles.modalBody}>
            <div className={emailStyles.container}>
              <div className={emailStyles.content}>
                <div className={emailStyles.pollingSection}>
                  <div className={emailStyles.pollingHeader}>
                    <LoadingSpinner size="md" variant="primary" label={subtitle} />
                    <span className={emailStyles.pollingText}>{subtitle}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase.kind === 'picking_account') {
    return (
      <MetaAccountPicker
        accounts={phase.accounts}
        state={stateToken!}
        // Link succeeded → kick into the syncing phase, which fires the
        // completeSetup useEffect above. Final transition to 'success'
        // happens inside that effect once the backfill trigger resolves
        // (or times out, which we treat as success).
        onLinked={(account) => setSyncingAccount(account)}
        onError={(err) => setLinkError(bucketFromAxios(err))}
      />
    );
  }

  if (phase.kind === 'success') {
    return (
      <MetaLinkSuccess account={phase.account} onContinue={goToDashboard} />
    );
  }

  // Only wire onRetry for buckets where retrying the same call has any chance
  // of working. For state-related buckets (state token consumed / invalid)
  // the only meaningful action is starting over from /account, so we hide
  // Try Again entirely via canRetry=false. For meta_api_error / unknown we
  // refetch the accounts query in place; if the link step itself failed
  // (linkError populated), retry means clearing that and letting the user
  // re-pick an account from the same accounts list.
  const canRetry = RETRYABLE_BUCKETS.has(phase.error);
  const handleRetry = () => {
    if (linkError?.kind === 'setup_failed') {
      // Re-fire completeSetup against the same account. The credential
      // row already exists from the prior linkAccount call; just bump
      // the attempt counter to re-trigger the useEffect.
      setLinkError(null);
      setSetupAttempt((n) => n + 1);
      return;
    }
    if (linkError) {
      setLinkError(null);
      return;
    }
    void accountsQuery.refetch();
  };

  // Terminal-from-callback buckets → /account. Retrying from here would
  // either be pointless (gate/threshold_rejected — retry fails identically
  // or the credential is already deleted) or unsafe (setup_indeterminate
  // — the user needs to see the actual live state, not restart in place).
  // All other error buckets → / ("Back to Dashboard").
  // All success=false paths are terminal (backend hard-deletes on any
  // reject; retry-in-place would 404). `unknown` is included so a
  // future backend reason_kind we don't yet map doesn't accidentally
  // become retryable — better to send the user to /account to see
  // the live state than to loop them through a broken retry.
  const backToAccountBuckets = new Set([
    'gate',
    'threshold_rejected',
    'currency_rejected',
    'already_connected',
    'setup_indeterminate',
    'sync_failed',
    'verify_failed',
    'unknown',
  ]);
  const onCloseTarget = backToAccountBuckets.has(phase.error)
    ? goToAccount
    : goToDashboard;

  return (
    <MetaLinkError
      error={phase.error}
      serverMessage={phase.serverMessage}
      canRetry={canRetry}
      onRetry={handleRetry}
      onClose={onCloseTarget}
    />
  );
}
